import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabaseClient";
import {
  HOBBLI_SUPPORT_ID,
  formatAdminDate,
  getSupabaseErrorMessage,
} from "./adminConfig";

export default function AdminMessages({ onUnreadCountChange }) {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");

    const { data, error } = await supabase
      .from("messages")
      .select("*")
      .eq("receiver_id", HOBBLI_SUPPORT_ID)
      .order("read_at", { ascending: true, nullsFirst: true })
      .order("created_at", { ascending: false });

    if (error) {
      console.error("FETCH MESSAGES ERROR:", error);
      setErrorMessage(getSupabaseErrorMessage(error, "Unable to load messages."));
      setLoading(false);
      return;
    }

    const rows = data || [];
    setMessages(rows);

    setSelectedMessage((current) => {
      if (!current) return null;
      return rows.find((row) => row.id === current.id) || null;
    });

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const markAsRead = async (messageId) => {
    const readAt = new Date().toISOString();

    setMessages((prev) =>
      prev.map((message) =>
        message.id === messageId ? { ...message, read_at: readAt } : message
      )
    );

    setSelectedMessage((prev) =>
      prev?.id === messageId ? { ...prev, read_at: readAt } : prev
    );

    const { data, error } = await supabase
      .from("messages")
      .update({ read_at: readAt })
      .eq("id", messageId)
      .eq("receiver_id", HOBBLI_SUPPORT_ID)
      .select("id, read_at");

    const updatedMessage = data?.[0] || null;

    if (error || !updatedMessage?.read_at) {
      const reason = error
        ? getSupabaseErrorMessage(error)
        : "Supabase accepted the request but did not return an updated row. This usually means the UPDATE is being blocked by RLS or a policy condition.";

      console.error("Message read state did not persist:", error || reason);
      setErrorMessage(`Could not persist read state: ${reason}`);
      await fetchMessages();
      return false;
    }

    setMessages((prev) =>
      prev.map((message) =>
        message.id === messageId
          ? { ...message, read_at: updatedMessage.read_at }
          : message
      )
    );
    setSelectedMessage((prev) =>
      prev?.id === messageId
        ? { ...prev, read_at: updatedMessage.read_at }
        : prev
    );

    return true;
  };

  const openMessage = async (message) => {
    setSelectedMessage(message);
    setErrorMessage("");
    setStatusMessage("");

    if (!message.read_at) {
      await markAsRead(message.id);
    }
  };

  const markAllRead = async () => {
    const unreadIds = messages.filter((message) => !message.read_at).map((message) => message.id);
    if (!unreadIds.length) return;

    setSaving(true);
    setErrorMessage("");
    setStatusMessage("");

    const readAt = new Date().toISOString();
    const { data, error } = await supabase
      .from("messages")
      .update({ read_at: readAt })
      .eq("receiver_id", HOBBLI_SUPPORT_ID)
      .is("read_at", null)
      .select("id, read_at");

    if (error) {
      console.error("Unable to mark all messages as read:", error);
      setErrorMessage(getSupabaseErrorMessage(error, "Unable to mark all messages as read."));
    } else if (!data?.length) {
      setErrorMessage(
        "No message rows were updated. If unread messages still exist, check the UPDATE policy on the messages table."
      );
    } else {
      setStatusMessage(`${data.length} message${data.length === 1 ? "" : "s"} marked as read.`);
    }

    await fetchMessages();
    setSaving(false);
  };

  const filteredMessages = useMemo(() => {
    const term = search.trim().toLowerCase();

    return messages.filter((message) => {
      if (unreadOnly && message.read_at) return false;
      if (!term) return true;

      const haystack = [
        message.content,
        message.metadata?.item_name,
        message.metadata?.email,
        message.metadata?.name,
        message.type,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [messages, search, unreadOnly]);

  const unreadCount = useMemo(
    () => messages.filter((message) => !message.read_at).length,
    [messages]
  );

  useEffect(() => {
    onUnreadCountChange?.(unreadCount);
  }, [onUnreadCountChange, unreadCount]);

  return (
    <div>
      <div className="hadmin-toolbar">
        <input
          className="hadmin-input hadmin-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search subject, email, or message text..."
        />

        <label className="hadmin-pill" style={{ cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(event) => setUnreadOnly(event.target.checked)}
          />
          Unread only
        </label>

        <button
          type="button"
          className="hadmin-btn-secondary"
          disabled={!unreadCount || saving}
          onClick={markAllRead}
        >
          {saving ? "Saving..." : `Mark All Read (${unreadCount})`}
        </button>

        <button type="button" className="hadmin-btn-secondary" onClick={fetchMessages}>
          Refresh
        </button>
      </div>

      {errorMessage && <div className="hadmin-error">{errorMessage}</div>}
      {statusMessage && <div className="hadmin-success">{statusMessage}</div>}

      {loading ? (
        <div className="hadmin-panel">Loading messages...</div>
      ) : !messages.length ? (
        <div className="hadmin-panel hadmin-empty">No support messages yet.</div>
      ) : (
        <div className="hadmin-split">
          <section className="hadmin-panel" style={{ padding: 12 }}>
            <div className="hadmin-row-between" style={{ padding: "4px 4px 12px" }}>
              <strong>Inbox</strong>
              <span className="hadmin-pill blue">{unreadCount} unread</span>
            </div>

            <div className="hadmin-list" style={{ maxHeight: "calc(100vh - 250px)", overflowY: "auto" }}>
              {filteredMessages.length ? (
                filteredMessages.map((message) => (
                  <button
                    type="button"
                    key={message.id}
                    className={`hadmin-list-item ${!message.read_at ? "unread" : ""} ${
                      selectedMessage?.id === message.id ? "selected" : ""
                    }`}
                    onClick={() => openMessage(message)}
                  >
                    <div className="hadmin-row-between">
                      <strong>{message.metadata?.item_name || "General Inquiry"}</strong>
                      {!message.read_at && <span className="hadmin-pill blue">New</span>}
                    </div>

                    <div className="hadmin-muted" style={{ marginTop: 4, fontSize: 11 }}>
                      {message.metadata?.email || "Unknown sender"} • {formatAdminDate(message.created_at)}
                    </div>

                    <div style={{ marginTop: 7, color: "#cbd5e1", fontSize: 12, lineHeight: 1.4 }}>
                      {message.content?.slice(0, 135)}
                      {message.content?.length > 135 ? "…" : ""}
                    </div>
                  </button>
                ))
              ) : (
                <div className="hadmin-empty">No messages match the current filter.</div>
              )}
            </div>
          </section>

          <section className="hadmin-panel">
            {selectedMessage ? (
              <>
                <div className="hadmin-section-heading">
                  <div>
                    <h2>{selectedMessage.metadata?.item_name || "Message"}</h2>
                    <p>{formatAdminDate(selectedMessage.created_at)}</p>
                  </div>

                  {selectedMessage.read_at && <span className="hadmin-pill green">Read</span>}
                </div>

                <div className="hadmin-detail-grid" style={{ marginBottom: 16 }}>
                  <div className="hadmin-detail-box">
                    <span>Email</span>
                    <div>{selectedMessage.metadata?.email || "—"}</div>
                  </div>
                  <div className="hadmin-detail-box">
                    <span>Type</span>
                    <div>{selectedMessage.type || selectedMessage.metadata?.type || "—"}</div>
                  </div>
                  <div className="hadmin-detail-box">
                    <span>Message ID</span>
                    <div style={{ wordBreak: "break-all" }}>{selectedMessage.id}</div>
                  </div>
                  <div className="hadmin-detail-box">
                    <span>Read At</span>
                    <div>{formatAdminDate(selectedMessage.read_at)}</div>
                  </div>
                </div>

                <div className="hadmin-card" style={{ background: "#0f141c" }}>
                  <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, color: "#e2e8f0" }}>
                    {selectedMessage.content || "No message body."}
                  </div>
                </div>

                <div className="hadmin-actions" style={{ marginTop: 14 }}>
                  {selectedMessage.metadata?.email && (
                    <a
                      className="hadmin-btn"
                      href={`mailto:${selectedMessage.metadata.email}`}
                      style={{ textDecoration: "none" }}
                    >
                      Reply by Email
                    </a>
                  )}
                </div>
              </>
            ) : (
              <div className="hadmin-empty">Select a message to read it.</div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
